import React from 'react';
import { FaExclamationTriangle, FaExchangeAlt } from 'react-icons/fa';
import PlantafelSidePanel from './PlantafelSidePanel';
import PlantafelConflictModal from './PlantafelConflictModal';
import TeamModal from './TeamModal';
import WeeklyRosterModal from './WeeklyRosterModal';
import CreateJobModal from './CreateJobModal';
import ScheduleTaskModal from './ScheduleTaskModal';
import CreateProjectModal from './CreateProjectModal';
import { CrmTask } from '../../sales/services/CrmTaskService';
import { Shift } from '../../shifts/services/ShiftService';
import { LaneTeam, LaneGroup, EmployeeJobConflict } from '../services/PlantafelService';
import { Absence } from '../../absences/services/AbsenceService';
import styles from './PlantafelBoard.module.css';

interface PlantafelBoardModalsProps {
  sidePanelOpen: boolean;
  selectedShift: Shift | null;
  selectedTask: CrmTask | null;
  closeSidePanel: () => void;
  refresh: () => Promise<void>;
  teamModalOpen: boolean;
  selectedWorkArea: LaneGroup | null;
  editingTeam: LaneTeam | null;
  handleTeamModalClose: () => void;
  handleTeamSaved: () => Promise<void>;
  rosterModalOpen: boolean;
  selectedTeamForRoster: LaneTeam | null;
  weekStartStr: string;
  handleRosterModalClose: () => void;
  handleRosterSaved: () => Promise<void>;
  conflictState: {
    isOpen: boolean;
    conflicts: any[];
    pendingSchedule: { taskTitle?: string; startDate?: string } | null;
  };
  closeConflictModal: () => void;
  pushConflicts: () => Promise<import('../services/PlantafelService').MovedShift[]>;
  employeeConflictModal: {
    employeeId: number;
    employeeName: string;
    targetShiftId: number;
    conflict: EmployeeJobConflict;
  } | null;
  setEmployeeConflictModal: (state: null) => void;
  handleReplanEmployee: () => Promise<void>;
  createJobModal: { teamId: number; dateStr: string } | null;
  allTeams: LaneTeam[];
  handleCreateJobModalClose: () => void;
  handleJobCreated: (shift: Shift) => Promise<void>;
  scheduleModal: { isOpen: boolean; task: CrmTask | null };
  laneGroups: LaneGroup[];
  days: any[];
  closeScheduleModal: () => void;
  scheduleBacklogTask: any;
  createProjectModalOpen: boolean;
  setCreateProjectModalOpen: (open: boolean) => void;
  handleProjectCreated: (project: any) => Promise<void>;
  absModal: {
    open: boolean;
    date?: string;
    type?: 'approved' | 'requested';
    anchor?: { top: number; left: number };
  };
  setAbsModal: (state: { open: boolean }) => void;
  absences: Absence[];
}

export function PlantafelBoardModals({
  sidePanelOpen,
  selectedShift,
  selectedTask,
  closeSidePanel,
  refresh,
  teamModalOpen,
  selectedWorkArea,
  editingTeam,
  handleTeamModalClose,
  handleTeamSaved,
  rosterModalOpen,
  selectedTeamForRoster,
  weekStartStr,
  handleRosterModalClose,
  handleRosterSaved,
  conflictState,
  closeConflictModal,
  pushConflicts,
  employeeConflictModal,
  setEmployeeConflictModal,
  handleReplanEmployee,
  createJobModal,
  allTeams,
  handleCreateJobModalClose,
  handleJobCreated,
  scheduleModal,
  laneGroups,
  days,
  closeScheduleModal,
  scheduleBacklogTask,
  createProjectModalOpen,
  setCreateProjectModalOpen,
  handleProjectCreated,
  absModal,
  setAbsModal,
  absences,
}: PlantafelBoardModalsProps): React.JSX.Element {
  return (
    <>
      {sidePanelOpen && (
        <PlantafelSidePanel
          shift={selectedShift}
          task={selectedTask}
          onClose={closeSidePanel}
          onRefresh={refresh}
        />
      )}

      {teamModalOpen && selectedWorkArea && (
        <TeamModal
          workArea={selectedWorkArea}
          team={editingTeam}
          onClose={handleTeamModalClose}
          onSaved={handleTeamSaved}
        />
      )}

      {rosterModalOpen && selectedTeamForRoster && (
        <WeeklyRosterModal
          team={selectedTeamForRoster}
          initialWeek={weekStartStr}
          onClose={handleRosterModalClose}
          onSaved={handleRosterSaved}
        />
      )}

      <PlantafelConflictModal
        isOpen={conflictState.isOpen}
        conflictType="workArea"
        workAreaConflicts={conflictState.conflicts}
        entityName={conflictState.pendingSchedule?.taskTitle || 'Job'}
        shiftDate={conflictState.pendingSchedule?.startDate || ''}
        onConfirm={closeConflictModal}
        onCancel={closeConflictModal}
        onPushConflicts={pushConflicts}
      />

      {employeeConflictModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setEmployeeConflictModal(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={styles.modalContent}
          >
            <div className={styles.modalHeader}>
              <div className={styles.modalIconBox}>
                <FaExclamationTriangle size={18} color="#d97706" />
              </div>
              <div>
                <h3 className={styles.modalTitle}>Mitarbeiter-Konflikt</h3>
                <div className={styles.modalSubtitle}>{employeeConflictModal.employeeName}</div>
              </div>
            </div>

            <div className={styles.conflictBox}>
              <div className={styles.conflictLabel}>Bereits eingeplant in:</div>
              <div className={styles.conflictJobTitle}>
                {employeeConflictModal.conflict.crm_task_title || 'Job'}
              </div>
              <div className={styles.conflictTeamName}>
                {employeeConflictModal.conflict.work_area_team_name}
              </div>
              <div className={styles.conflictTime}>
                {new Date(employeeConflictModal.conflict.start_time).toLocaleString('de-DE', {
                  weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                })}
                {' – '}
                {new Date(employeeConflictModal.conflict.end_time).toLocaleString('de-DE', {
                  hour: '2-digit', minute: '2-digit',
                })}
              </div>
            </div>

            <div className={styles.modalQuestion}>
              Möchtest du <strong>{employeeConflictModal.employeeName}</strong> aus dem aktuellen Job entfernen und dem neuen Job zuweisen?
            </div>

            <div className={styles.modalActions}>
              <button onClick={() => setEmployeeConflictModal(null)} className={styles.cancelButton}>
                Abbrechen
              </button>
              <button onClick={handleReplanEmployee} className={styles.replanButton}>
                <FaExchangeAlt size={12} />
                Jetzt umplanen
              </button>
            </div>
          </div>
        </div>
      )}

      <CreateJobModal
        isOpen={createJobModal !== null}
        defaultTeamId={createJobModal?.teamId || 0}
        defaultDateStr={createJobModal?.dateStr || ''}
        teams={allTeams}
        onClose={handleCreateJobModalClose}
        onCreated={handleJobCreated}
      />

      {scheduleModal.task && (
        <ScheduleTaskModal
          isOpen={scheduleModal.isOpen}
          task={scheduleModal.task}
          laneGroups={laneGroups}
          days={days}
          onClose={closeScheduleModal}
          onSchedule={scheduleBacklogTask}
        />
      )}

      <CreateProjectModal
        isOpen={createProjectModalOpen}
        onClose={() => setCreateProjectModalOpen(false)}
        onCreated={handleProjectCreated}
      />

      {absModal.open && absModal.date && (
        <div
          className={styles.absenceModalOverlay}
          onClick={() => setAbsModal({ open: false })}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={styles.absenceModalContent}
            style={{
              top: absModal.anchor?.top ?? '50%',
              left: absModal.anchor?.left ?? '50%',
              transform: absModal.anchor ? 'none' : 'translate(-50%, -50%)',
              width: 320,
            }}
          >
            <button
              onClick={() => setAbsModal({ open: false })}
              className={styles.absenceCloseButton}
            >
              x
            </button>
            <div style={{ fontSize: '14px' }}>
              {(() => {
                const dayISO = absModal.date!;
                const ds = new Date(`${dayISO}T00:00:00`).getTime();
                const de = new Date(`${dayISO}T23:59:59`).getTime();
                const items = absences.filter(a => {
                  const as = new Date(a.start_datetime).getTime();
                  const ae = new Date(a.end_datetime).getTime();
                  const statusMatch = absModal.type === 'approved'
                    ? a.status === 'APPROVED'
                    : a.status === 'REQUESTED';
                  return statusMatch && as <= de && ae >= ds;
                });

                if (items.length === 0) {
                  return <div className={styles.absenceNoItems}>Keine Abwesenheiten</div>;
                }

                return (
                  <div className={styles.absenceListCol}>
                    {items.map((item) => (
                      <div key={item.id} className={styles.absenceItemRow}>
                        <div
                          className={styles.absenceStatusDot}
                          style={{ background: item.status === 'APPROVED' ? '#dc3545' : '#f59e0b' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div className={styles.absenceItemName}>
                            {item.employee_name || `Mitarbeiter #${item.employee}`}
                          </div>
                          <div className={styles.absenceItemType}>
                            {item.absence_type === 'HOLIDAY' && 'Urlaub'}
                            {item.absence_type === 'SICK' && 'Krank'}
                            {item.absence_type === 'SICK_CHILD' && 'Kind krank'}
                            {item.absence_type === 'OVERTIME' && 'Überstundenabbau'}
                            {item.absence_type === 'EXAM' && 'Prüfung'}
                            {item.absence_type === 'WISH_OFF' && 'Wunschfrei'}
                          </div>
                        </div>
                        <div className={styles.absenceItemDate}>
                          {new Date(item.start_datetime).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                          {' - '}
                          {new Date(item.end_datetime).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div className={styles.absenceFooter}>
              <button onClick={() => setAbsModal({ open: false })} className={styles.absenceCloseBtn}>
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes pulse-glow {
          0% { box-shadow: 0 0 8px 2px rgba(220, 53, 69, 0.6); }
          50% { box-shadow: 0 0 12px 4px rgba(220, 53, 69, 0.8); }
          100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
        }
      `}</style>
    </>
  );
}
